/* eslint-disable react-hooks/exhaustive-deps */
import { getAreas } from 'api/common';
import { getFirstTownOfCityCode } from 'api/user';
import useAreaCookie from 'hooks/useAreaCookie';
import { LocContext } from 'libs/contexts';
import { setLocalStorage } from 'libs/utils';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Popup } from 'react-vant';
import { cls } from 'reactutils';
import IPSwitchModal from './IPSwitchModal';
import { forEachRight } from 'lodash';

interface AreaItem {
  sysNo: string;
  parentSysNo: string;
  hasChild: boolean;
  name: string;
  active: boolean;
  type: number;
}

type QueryParam = {
  sysNo?: string;
  parentSysNo?: string;
  type?: number;
};

type AreaCascaderProps = {
  onChange?: (_value: any[]) => void; //回调 返回已选区域sysNo集合 ['1150', '1165']
  value?: string | string[]; //默认值  '1165' | ['1150', '1165'](取数组最后一个值)
  areaLevel?: number; // 区域显示层级  默认：2 (市级)  目前区域api最大4级
  separator?: string; // 分隔符 默认 /
  styles?: string;
  onClose: () => void;
  visible: boolean;
  onInit?: (_value: any[]) => void; //回调 返回已选区域sysNo集合 ['1150', '1165']
  enableIPSwitch?: boolean;
};

async function getAreaList(param: QueryParam) {
  const resp: any = await getAreas({
    method: 'POST',
    json: {
      loadAllArea: true,
      ...param,
    },
  });
  if (resp.code === 200) {
    const { province, city, region, town } = resp.data;
    const data = [province, city, region, town].filter(
      (item) => item instanceof Array && item.length,
    );
    return data;
  } else {
    return [];
  }
}

export default function AreaPickerWithLevel({
  onChange,
  onClose,
  visible,
  areaLevel = 4,
  separator = ' / ',
  onInit,
  enableIPSwitch = false,
}: AreaCascaderProps) {
  const [paramSysNo, setParamSysNo] = useState('');
  const [area, setArea] = useState<any>([]);
  const [selectArea, setSelectArea] = useState<any>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [fullAreaName, setFullAreaName] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);
  // NOTE: 获取当前地理位置
  const { location, locKey, setLocation } = useContext(LocContext);
  const { AREA_CODE_KEY, AREA_SYS_NO_KEY, setCookie } = useAreaCookie();

  useEffect(() => {
    let sysNo = '';
    /**
     * 兼容AreasPicker数组传参
     * 判断value是否是数组 是数组取最后一位
     */
    if (location.sysNo) {
      if (location.sysNo instanceof Array) {
        sysNo = location.sysNo[location.sysNo.length - 1];
      } else {
        sysNo = location.sysNo as string;
      }
      if (sysNo) setParamSysNo(sysNo);
    }
  }, [location]);

  // 执行两遍
  // step1.初始化地理选择器下拉框
  // step2.浏览器设置定位后，执行onInit进行回显LocContextProvider设置地理位置（浏览器定位）
  useEffect(() => {
    const param: QueryParam = {};

    if (paramSysNo) {
      param.sysNo = paramSysNo;
    } else {
      param.type = 1;
    }
    if (!paramSysNo && areaLevel === 1) return;
    // 首次进入时，paramSysNo 为空
    getAreaList(param).then((data) => {
      // areaList [省、市、区、镇]二维数组
      const areaList: any = paramSysNo ? dataAdapter(data) : data;
      let selectList = filterSelectArea(areaList);
      if (selectList.length <= 0) {
        selectList.push({
          name: '请选择地址',
          sysNo: null,
        });
      } else {
        selectList = selectList.splice(0, areaLevel);
      }
      setActiveIndex(selectList.length - 1);
      setSelectArea(selectList);
      setArea(areaList);
      // 本地存储中有值（LocContextProvider操作），定位选择器回显
      if (paramSysNo) {
        setFullAreaName(selectList.map((area: AreaItem) => area.name).join(separator));
        onInit?.(selectList);
      } else {
        setFullAreaName('');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramSysNo]);

  const handleChange = useCallback(async (selectData: any[]) => {
    setFullAreaName(selectData.map((area: AreaItem) => area.name).join(separator));
    const res = await getFirstTownOfCityCode(selectData[selectData.length - 1]?.sysNo);
    const loc = {
      sysNo: res.data?.sysNo,
      areaCode: res.data?.levelCode,
    };
    setLocation({ ...loc });
    // NOTE: 用户选择后更新localstorage
    setLocalStorage(locKey, { ...loc });
    setCookie(AREA_CODE_KEY, res.data?.levelCode);
    setCookie(AREA_SYS_NO_KEY, res.data?.sysNo);
    // setVisible(false)
    onChange && onChange(selectData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadArea = useCallback(
    (item: { hasChild: any; name: any; sysNo: any; active: boolean }, index: number) => {
      const tempArea = area.concat();
      const filterArea: any = tempArea.splice(0, index + 1);
      const tempSelectArea: any = [...selectArea];
      // 重置active状态
      filterArea[filterArea.length - 1].forEach((item: AreaItem) => (item.active = false));
      if (!item.hasChild || index + 1 >= areaLevel) {
        tempSelectArea.pop();
        tempSelectArea.push({
          name: item.name,
          sysNo: item.sysNo,
        });
        item.active = true;
        setSelectArea(tempSelectArea);
        return handleChange(tempSelectArea);
      }
      // 正向查询
      getAreaList({ parentSysNo: item.sysNo, type: index + 1 }).then((data) => {
        const dataList = filterArea.concat(data);
        const areaList: any = dataAdapter(dataList);
        const selectList = filterSelectArea(areaList);
        if (selectList.length < areaLevel) {
          selectList.push({
            name: '请选择地址',
            sysNo: null,
          });
        }
        setSelectArea(selectList);
        setArea(areaList);
        setTimeout(() => {
          setActiveIndex(selectList.length - 1);
        }, 200);
      });
    },
    [area, selectArea],
  );

  const handleChangeTab = useCallback((index: number | ((_prevState: number) => number)) => {
    setActiveIndex(index);
  }, []);

  // 通知使用 IP 定位展示
  const handleChangeToIP = (cityInfo) => {
    handleChange([cityInfo]);
  };

  return (
    <>
      <Popup
        visible={visible}
        onClose={onClose}
        position="bottom"
        transition="simple-bottom-transition"
      >
        <div className="headWrap">
          <div>
            {selectArea.map((item: any, index: number) => (
              <div
                onClick={() => handleChangeTab(index)}
                className={cls('headAreaItem', activeIndex === index && 'active')}
                key={index}
              >
                {item.name}
              </div>
            ))}
          </div>
        </div>
        <div className="bottomContent" ref={contentRef}>
          {area[activeIndex]?.map((item: any, index: any) => (
            <div
              className={cls(
                'bottomItem',
                selectArea?.[activeIndex]?.sysNo === item.sysNo && 'active',
                paramSysNo === item.sysNo && 'active',
              )}
              key={`${index}-${item.sysNo}`}
              onClick={() => handleLoadArea(item, activeIndex)}
            >
              {item.name}
            </div>
          ))}
        </div>
      </Popup>
      {enableIPSwitch && (
        <IPSwitchModal fullAreaName={fullAreaName} onChange={handleChangeToIP} value={location} />
      )}
      <style jsx>{`
        .headWrap {
          height: 54px;
          line-height: 54px;
          display: flex;
          justify-content: space-between;
          padding: 0 14px 0 10px;
          font-size: 14px;
          color: #2e2d37;
          border-bottom: 1px solid #dadada;
          background-color: #fff;
        }

        .headAreaItem {
          padding: 0 3px;
          display: inline-block;
          vertical-align: middle;
          margin-right: 12px;
          border-bottom: 3px solid transparent;
          line-height: 50px;
        }

        .headAreaItem.active {
          border-color: var(--primaryColor);
        }

        .bottomContent {
          height: 420px;
          overflow-y: auto;
          background-color: #fff;
        }

        .bottomItem {
          height: 55px;
          line-height: 1;
          padding: 20px 16px;
          color: #000000;
          font-size: 14px;
          text-align: left;
        }

        .bottomItem.active {
          color: var(--primaryColor);
          font-weight: bold;
        }
      `}</style>
    </>
  );
}

// 逆向找父节点
function dataAdapter(data: string | any[]) {
  forEachRight(data, (item, index) => {
    item.forEach((items) => {
      const { parentSysNo, active } = items || {};
      if (active) {
        items.active = true;
      }
      data[index - 1] &&
        data[index - 1].forEach((newItem) => {
          const { sysNo } = newItem || {};
          if (sysNo === parentSysNo) {
            newItem.active = true;
          }
        });
    });
  });
  return data;
}

function filterSelectArea(data: any[]) {
  return data.reduce((acc: { name: any; sysNo: any; code: any }[], item: any[]) => {
    const activeItem = item.filter((item: { active: any }) => item.active);
    if (activeItem.length > 0) {
      acc.push({
        name: activeItem[0].name,
        sysNo: activeItem[0].sysNo,
        code: activeItem[0].levelCode,
      });
    }
    return acc;
  }, []);
}
